import { computed, inject, ref, Ref, watch } from 'vue'

/**
 * Composable that provides form field validation plumbing.
 * Integrates with the provide/inject validation system (isValidationActive / setValidationResult).
 *
 * @param fieldId - Unique field identifier for validation tracking
 * @param validationFn - Function that returns an error message string (or null/empty if valid)
 */
export function useFieldValidation(fieldId: string, validationFn: () => string | null) {
    /********************************************************************************************************************
     INJECT
     ********************************************************************************************************************/
    const isValidationActive = inject<Ref<boolean>>('isValidationActive')
    const setValidationResult = inject<(id: string, isValid: boolean) => void>('setValidationResult')

    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const showError = ref(false)
    const errorMessage = ref('')

    /********************************************************************************************************************
     COMPUTED PROPERTIES
     ********************************************************************************************************************/
    const errorId = computed(() => showError.value ? `${fieldId}_error` : '')

    /********************************************************************************************************************
     WATCHERS
     ********************************************************************************************************************/
    if (isValidationActive) {
        watch(isValidationActive, (active) => {
            if (active) validate()
        }, { flush: 'sync' })
    }

    /********************************************************************************************************************
     METHODS
     ********************************************************************************************************************/
    function validate() {
        const msg = validationFn()

        showError.value = msg != null && msg !== ''
        errorMessage.value = msg ?? ''

        if (setValidationResult) {
            setValidationResult(fieldId, !showError.value)
        }
    }

    function clearError() {
        showError.value = false
    }

    return { showError, errorMessage, errorId, validate, clearError }
}
